import { createHash } from 'crypto-browserify'
import { HDNodeWallet, Mnemonic, getBytes, sha256 as ethersSha256 } from 'ethers'

/**
 * Normalize mnemonic for BIP-39 validation (required by Railgun SDK).
 * - Trim whitespace
 * - Collapse multiple spaces
 * - NFKD Unicode normalization per BIP-39 spec
 */
export function normalizeMnemonic(mnemonic: string): string {
  return mnemonic.trim().replace(/\s+/g, ' ').normalize('NFKD')
}

/**
 * Fold an "Advanced Mode" BIP-39 passphrase (the optional 25th word) into the
 * mnemonic that seeds the RAILGUN wallet.
 *
 * WHY: the RAILGUN SDK derives the wallet purely from the mnemonic WORDS — it
 * calls `Mnemonic.toSeed(mnemonic, password='')` internally and exposes no
 * passphrase parameter. So two accounts that share a seed phrase but differ only
 * by passphrase collide on the SAME RAILGUN address/walletId (the bug: a
 * passphrase import shows the no-passphrase account's private balance/history).
 *
 * FIX: when a passphrase is present, compute the real BIP-39 seed (which DOES
 * incorporate the passphrase) and hash it down to fresh 32-byte entropy, then
 * re-express that entropy as a valid 24-word BIP-39 mnemonic. Feeding THAT
 * synthetic mnemonic to the SDK yields a deterministic, unique-per-passphrase
 * RAILGUN wallet. Without a passphrase the original (normalized) mnemonic is
 * returned verbatim, so existing wallets keep their exact derivation — no
 * migration, no address change.
 */
export function deriveEffectiveRailgunMnemonic(mnemonic: string, passphrase?: string): string {
  const normalized = normalizeMnemonic(mnemonic)
  if (!passphrase) return normalized
  // 64-byte BIP-39 seed that includes the passphrase.
  const seedHex = Mnemonic.fromPhrase(normalized, passphrase).computeSeed()
  // 32-byte entropy -> valid 24-word BIP-39 mnemonic.
  const entropyHex = ethersSha256(seedHex)
  return Mnemonic.fromEntropy(getBytes(entropyHex)).phrase
}

/**
 * Derive encryption key from mnemonic and account index (32-byte hex).
 *
 * The optional passphrase is mixed in so a passphrase account encrypts its
 * RAILGUN wallet at rest under a different key than the no-passphrase one.
 * When no passphrase is supplied the input string is byte-identical to the
 * legacy form (`mnemonic:index`), so existing wallets load unchanged.
 */
export function deriveEncryptionKey(
  mnemonic: string,
  accountIndex: number,
  passphrase?: string
): string {
  const data = passphrase
    ? `${mnemonic}:${accountIndex}:${passphrase}`
    : `${mnemonic}:${accountIndex}`
  const hash = createHash('sha256').update(data, 'utf8').digest('hex')
  return hash
}

/**
 * Build the PUBLIC EOA signer (gas-payer / broadcaster) for self-relayed
 * shield/unshield/transfer txs from the raw seed phrase, applying the BIP-39
 * passphrase via the standard m/44'/60'/0'/0/0 path. Mirrors the previous
 * `Wallet.fromPhrase(mnemonic, provider)` exactly when no passphrase is set,
 * but derives the CORRECT funded address when one is — otherwise the broadcast
 * would come from an unrelated (unfunded) address.
 */
export function derivePublicSigner(
  mnemonic: string,
  passphrase: string | undefined,
  provider: import('ethers').Provider | null
): HDNodeWallet {
  const wallet = HDNodeWallet.fromPhrase(normalizeMnemonic(mnemonic), passphrase || undefined)
  return provider ? wallet.connect(provider) : wallet
}
